import React, { useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Textarea,
  VStack,
  HStack,
  Grid,
  GridItem,
  Switch,
  Box,
  useToast,
  Tag,
  InputGroup,
  InputLeftAddon,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  Heading,
  Divider,
  Flex,
  SimpleGrid,
} from '@chakra-ui/react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { produtoSchema } from '../schemas/produtoSchema'

function ModalEditarProduto({ isOpen, onClose, produto, isLoading, onSalvar, tiposImpressao = [] }) {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isDirty, isSubmitting },
  } = useForm({
    resolver: zodResolver(produtoSchema),
    defaultValues: {
      id_produto: '',
      id_usuario: 1, // Valor default
      cod_grupo: '',
      codprod: '',
      ref_fornecedor: '',
      produto: '',
      fornecedor: '',
      cod_inno: '',
      titulo_seo: '',
      ativo: 'S',
      ativo_site: 'S',
      link_produto: '',
      estoque: 0,
      qtd_min: 0,
      max_batidas: 0,
      prazo_min: 0,
      multiplo: 0,
      pedido_minimo: 0,
      outros_custos: 0,
      impressao: 0,
      tipo_impressao: 0,
      prioridade: 'N',
      observacoes: '',
    },
    mode: 'onChange', // Validar ao alterar para feedback imediato
  })

  // Atualizar com os valores do produto quando disponível
  useEffect(() => {
    if (produto) {
      // Certificar que os valores numéricos sejam tratados corretamente
      reset({
        id_produto: Number(produto.id) || '',
        id_usuario: Number(produto.id_usuario) || 1,
        cod_grupo: Number(produto.cod_grupo) || '',
        codprod: Number(produto.codprod) || '',
        ref_fornecedor: produto.ref_fornecedor || '',
        produto: produto.produto || '',
        fornecedor: produto.fornecedor || '',
        cod_inno: produto.cod_inno || '',
        titulo_seo: produto.titulo_seo || '',
        ativo: produto.ativo || 'S',
        ativo_site: produto.ativo_site || 'S',
        link_produto: produto.link_produto || '',
        estoque: Number(produto.estoque) || 0,
        qtd_min: Number(produto.qtd_min) || 0,
        max_batidas: Number(produto.max_batidas) || 0,
        prazo_min: Number(produto.prazo_min) || 0,
        multiplo: Number(produto.multiplo) || 0,
        pedido_minimo: Number(produto.pedido_minimo) || 0,
        outros_custos: Number(produto.outros_custos) || 0,
        impressao: Number(produto.impressao) || 0,
        tipo_impressao: Number(produto.tipo_impressao) || 0,
        prioridade: produto.prioridade || 'N',
        observacoes: produto.observacoes || '',
      })
    }
  }, [produto, reset])

  const onSubmit = async (data) => {
    try {
      await onSalvar(data)
    } catch (error) {
      console.error('Erro ao processar formulário:', error)
    }
  }

  // Estilização comum para os inputs
  const inputStyles = {
    size: 'sm',
    borderRadius: 'sm',
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl">
      <ModalOverlay />
      <ModalContent borderRadius="sm" maxW="90vw">
        <ModalHeader fontSize="lg" fontWeight="bold" pb={2} borderBottomWidth="1px" borderBottomColor="gray.100" display="flex" alignItems="center">
          Editar Produto
          {produto?.ref_fornecedor && (
            <Tag size="md" colorScheme="blue" ml={2} mt={0} borderRadius="sm">
              {produto.ref_fornecedor}
            </Tag>
          )}
          {produto?.id && (
            <Tag size="md" colorScheme="gray" ml={2} mt={0} borderRadius="sm">
              ID: {produto.id}
            </Tag>
          )}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <form id="editar-produto-form" onSubmit={handleSubmit(onSubmit)}>
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              {/* Coluna da esquerda */}
              <Box>
                {/* Seção de Identificação e Informações Básicas */}
                <Box mb={5}>
                  <Heading as="h3" size="sm" mb={3} color="gray.700">
                    Informações Básicas
                  </Heading>
                  <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={3}>
                    <GridItem>
                      <FormControl isInvalid={!!errors.ref_fornecedor} isRequired>
                        <FormLabel fontSize="xs" mb={1}>
                          Referência Fornecedor
                        </FormLabel>
                        <Input {...register('ref_fornecedor')} placeholder="Referência do produto" {...inputStyles} />
                        <FormErrorMessage fontSize="xs">{errors.ref_fornecedor?.message}</FormErrorMessage>
                      </FormControl>
                    </GridItem>

                    <GridItem>
                      <FormControl isInvalid={!!errors.cod_inno}>
                        <FormLabel fontSize="xs" mb={1}>
                          Código Innovation
                        </FormLabel>
                        <Input {...register('cod_inno')} placeholder="Código interno" {...inputStyles} />
                        <FormErrorMessage fontSize="xs">{errors.cod_inno?.message}</FormErrorMessage>
                      </FormControl>
                    </GridItem>

                    <GridItem colSpan={{ base: 1, md: 2 }}>
                      <FormControl isInvalid={!!errors.produto} isRequired>
                        <FormLabel fontSize="xs" mb={1}>
                          Nome do Produto
                        </FormLabel>
                        <Input {...register('produto')} placeholder="Nome do produto" {...inputStyles} />
                        <FormErrorMessage fontSize="xs">{errors.produto?.message}</FormErrorMessage>
                      </FormControl>
                    </GridItem>

                    <GridItem>
                      <FormControl isInvalid={!!errors.fornecedor} isRequired>
                        <FormLabel fontSize="xs" mb={1}>
                          Fornecedor
                        </FormLabel>
                        <Input {...register('fornecedor')} placeholder="Nome do fornecedor" {...inputStyles} />
                        <FormErrorMessage fontSize="xs">{errors.fornecedor?.message}</FormErrorMessage>
                      </FormControl>
                    </GridItem>

                    <GridItem>
                      <FormControl isInvalid={!!errors.cod_grupo}>
                        <FormLabel fontSize="xs" mb={1}>
                          Código do Grupo
                        </FormLabel>
                        <Input {...register('cod_grupo')} placeholder="Grupo" type="number" {...inputStyles} />
                        <FormErrorMessage fontSize="xs">{errors.cod_grupo?.message}</FormErrorMessage>
                      </FormControl>
                    </GridItem>

                    <GridItem>
                      <FormControl isInvalid={!!errors.codprod}>
                        <FormLabel fontSize="xs" mb={1}>
                          Código do Produto
                        </FormLabel>
                        <Input {...register('codprod')} placeholder="Código único" type="number" {...inputStyles} />
                        <FormErrorMessage fontSize="xs">{errors.codprod?.message}</FormErrorMessage>
                      </FormControl>
                    </GridItem>

                    <GridItem>
                      <FormControl isInvalid={!!errors.id_produto}>
                        <FormLabel fontSize="xs" mb={1}>
                          ID do Produto
                        </FormLabel>
                        <Input {...register('id_produto')} placeholder="ID do produto" type="number" {...inputStyles} />
                        <FormErrorMessage fontSize="xs">{errors.id_produto?.message}</FormErrorMessage>
                      </FormControl>
                    </GridItem>
                  </Grid>
                </Box>

                <Divider my={4} />

                {/* Seção de Estoque e Pedidos */}
                <Box mb={5}>
                  <Heading as="h3" size="sm" mb={3} color="gray.700">
                    Estoque e Pedidos
                  </Heading>
                  <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={3}>
                    <GridItem>
                      <FormControl isInvalid={!!errors.estoque}>
                        <FormLabel fontSize="xs" mb={1}>
                          Estoque
                        </FormLabel>
                        <Controller
                          name="estoque"
                          control={control}
                          render={({ field }) => (
                            <NumberInput
                              {...field}
                              min={0}
                              precision={0}
                              {...inputStyles}
                              value={field.value || 0}
                              onChange={(valueString) => field.onChange(Number(valueString))}
                            >
                              <NumberInputField />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          )}
                        />
                        <FormErrorMessage fontSize="xs">{errors.estoque?.message}</FormErrorMessage>
                      </FormControl>
                    </GridItem>

                    <GridItem>
                      <FormControl isInvalid={!!errors.qtd_min}>
                        <FormLabel fontSize="xs" mb={1}>
                          Quantidade Mínima
                        </FormLabel>
                        <Controller
                          name="qtd_min"
                          control={control}
                          render={({ field }) => (
                            <NumberInput
                              {...field}
                              min={0}
                              precision={0}
                              {...inputStyles}
                              onChange={(valueString) => field.onChange(parseInt(valueString) || 0)}
                            >
                              <NumberInputField />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          )}
                        />
                        <FormErrorMessage fontSize="xs">{errors.qtd_min?.message}</FormErrorMessage>
                      </FormControl>
                    </GridItem>

                    <GridItem>
                      <FormControl isInvalid={!!errors.multiplo}>
                        <FormLabel fontSize="xs" mb={1}>
                          Múltiplo
                        </FormLabel>
                        <Controller
                          name="multiplo"
                          control={control}
                          render={({ field }) => (
                            <NumberInput
                              {...field}
                              min={0}
                              precision={0}
                              {...inputStyles}
                              onChange={(valueString) => field.onChange(parseInt(valueString) || 0)}
                            >
                              <NumberInputField />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          )}
                        />
                        <FormErrorMessage fontSize="xs">{errors.multiplo?.message}</FormErrorMessage>
                      </FormControl>
                    </GridItem>

                    <GridItem>
                      <FormControl isInvalid={!!errors.pedido_minimo}>
                        <FormLabel fontSize="xs" mb={1}>
                          Pedido Mínimo
                        </FormLabel>
                        <Controller
                          name="pedido_minimo"
                          control={control}
                          render={({ field }) => (
                            <NumberInput
                              {...field}
                              min={0}
                              precision={0}
                              {...inputStyles}
                              onChange={(valueString) => field.onChange(parseInt(valueString) || 0)}
                            >
                              <NumberInputField />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          )}
                        />
                        <FormErrorMessage fontSize="xs">{errors.pedido_minimo?.message}</FormErrorMessage>
                      </FormControl>
                    </GridItem>
                  </Grid>
                </Box>

                <Divider my={4} />

                {/* Seção de Status e SEO */}
                <Box mb={5}>
                  <SimpleGrid columns={2} spacing={5}>
                    <Box>
                      <Heading as="h3" size="sm" mb={3} color="gray.700">
                        Status
                      </Heading>
                      <Grid gap={2}>
                        <GridItem>
                          <FormControl>
                            <FormLabel fontSize="xs" mb={1}>
                              Ativo
                            </FormLabel>
                            <Controller
                              name="ativo"
                              control={control}
                              render={({ field: { onChange, value } }) => (
                                <HStack spacing={4}>
                                  <Switch
                                    isChecked={value === 'S'}
                                    onChange={(e) => onChange(e.target.checked ? 'S' : 'N')}
                                    colorScheme="green"
                                    size="sm"
                                  />
                                  <Box fontSize="xs">{value === 'S' ? 'Ativo' : 'Inativo'}</Box>
                                </HStack>
                              )}
                            />
                          </FormControl>
                        </GridItem>

                        <GridItem>
                          <FormControl>
                            <FormLabel fontSize="xs" mb={1}>
                              Ativo no Site
                            </FormLabel>
                            <Controller
                              name="ativo_site"
                              control={control}
                              render={({ field: { onChange, value } }) => (
                                <HStack spacing={4}>
                                  <Switch
                                    isChecked={value === 'S'}
                                    onChange={(e) => onChange(e.target.checked ? 'S' : 'N')}
                                    colorScheme="blue"
                                    size="sm"
                                  />
                                  <Box fontSize="xs">{value === 'S' ? 'Visível no site' : 'Oculto no site'}</Box>
                                </HStack>
                              )}
                            />
                          </FormControl>
                        </GridItem>

                        <GridItem>
                          <FormControl>
                            <FormLabel fontSize="xs" mb={1}>
                              Prioridade
                            </FormLabel>
                            <Controller
                              name="prioridade"
                              control={control}
                              render={({ field: { onChange, value } }) => (
                                <HStack spacing={4}>
                                  <Switch
                                    isChecked={value === 'S'}
                                    onChange={(e) => onChange(e.target.checked ? 'S' : 'N')}
                                    colorScheme="purple"
                                    size="sm"
                                  />
                                  <Box fontSize="xs">{value === 'S' ? 'Prioritário' : 'Normal'}</Box>
                                </HStack>
                              )}
                            />
                          </FormControl>
                        </GridItem>
                      </Grid>
                    </Box>

                    <Box>
                      <Heading as="h3" size="sm" mb={3} color="gray.700">
                        SEO
                      </Heading>
                      <FormControl isInvalid={!!errors.titulo_seo}>
                        <FormLabel fontSize="xs" mb={1}>
                          Título SEO
                        </FormLabel>
                        <Input {...register('titulo_seo')} placeholder="Título otimizado para SEO" {...inputStyles} />
                        <FormErrorMessage fontSize="xs">{errors.titulo_seo?.message}</FormErrorMessage>
                      </FormControl>
                    </Box>
                  </SimpleGrid>
                </Box>
              </Box>

              {/* Coluna da direita */}
              <Box>
                {/* Seção de Links e Recursos */}
                <Box mb={5}>
                  <Heading as="h3" size="sm" mb={3} color="gray.700">
                    Links e Recursos
                  </Heading>
                  <FormControl isInvalid={!!errors.link_produto}>
                    <FormLabel fontSize="xs" mb={1}>
                      Link do Produto
                    </FormLabel>
                    <Input {...register('link_produto')} placeholder="https://site-do-fornecedor.com/produto" {...inputStyles} />
                    <FormErrorMessage fontSize="xs">{errors.link_produto?.message}</FormErrorMessage>
                  </FormControl>
                </Box>

                <Divider my={4} />

                {/* Seção de Produção */}
                <Box mb={5}>
                  <Heading as="h3" size="sm" mb={3} color="gray.700">
                    Produção
                  </Heading>
                  <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={3}>
                    <GridItem>
                      <FormControl isInvalid={!!errors.max_batidas}>
                        <FormLabel fontSize="xs" mb={1}>
                          Máximo de Batidas
                        </FormLabel>
                        <Controller
                          name="max_batidas"
                          control={control}
                          render={({ field }) => (
                            <NumberInput
                              {...field}
                              min={0}
                              precision={0}
                              {...inputStyles}
                              onChange={(valueString) => field.onChange(parseInt(valueString) || 0)}
                            >
                              <NumberInputField />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          )}
                        />
                        <FormErrorMessage fontSize="xs">{errors.max_batidas?.message}</FormErrorMessage>
                      </FormControl>
                    </GridItem>

                    <GridItem>
                      <FormControl isInvalid={!!errors.prazo_min}>
                        <FormLabel fontSize="xs" mb={1}>
                          Prazo Mínimo (dias)
                        </FormLabel>
                        <Controller
                          name="prazo_min"
                          control={control}
                          render={({ field }) => (
                            <NumberInput
                              {...field}
                              min={0}
                              precision={0}
                              {...inputStyles}
                              onChange={(valueString) => field.onChange(parseInt(valueString) || 0)}
                            >
                              <NumberInputField />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          )}
                        />
                        <FormErrorMessage fontSize="xs">{errors.prazo_min?.message}</FormErrorMessage>
                      </FormControl>
                    </GridItem>

                    <GridItem>
                      <FormControl isInvalid={!!errors.impressao}>
                        <FormLabel fontSize="xs" mb={1}>
                          Qtd. Impressões
                        </FormLabel>
                        <Controller
                          name="impressao"
                          control={control}
                          render={({ field }) => (
                            <NumberInput
                              {...field}
                              min={0}
                              precision={0}
                              {...inputStyles}
                              onChange={(valueString) => field.onChange(parseInt(valueString) || 0)}
                            >
                              <NumberInputField />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          )}
                        />
                        <FormErrorMessage fontSize="xs">{errors.impressao?.message}</FormErrorMessage>
                      </FormControl>
                    </GridItem>

                    <GridItem>
                      <FormControl isInvalid={!!errors.tipo_impressao}>
                        <FormLabel fontSize="xs" mb={1}>
                          Tipo de Impressão
                        </FormLabel>
                        <Controller
                          name="tipo_impressao"
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              placeholder="Selecione o tipo"
                              {...inputStyles}
                              onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                            >
                              {tiposImpressao.map((tipo) => (
                                <option key={tipo.id} value={tipo.id}>
                                  {tipo.nome}
                                </option>
                              ))}
                            </Select>
                          )}
                        />
                        <FormErrorMessage fontSize="xs">{errors.tipo_impressao?.message}</FormErrorMessage>
                      </FormControl>
                    </GridItem>

                    <GridItem colSpan={{ base: 1, md: 2 }}>
                      <FormControl isInvalid={!!errors.outros_custos}>
                        <FormLabel fontSize="xs" mb={1}>
                          Outros Custos
                        </FormLabel>
                        <InputGroup size="sm">
                          <InputLeftAddon children="R$" borderRadius="sm" />
                          <Controller
                            name="outros_custos"
                            control={control}
                            render={({ field }) => (
                              <NumberInput
                                {...field}
                                min={0}
                                precision={2}
                                w="full"
                                borderLeftRadius={0}
                                {...inputStyles}
                                onChange={(valueString) => field.onChange(parseFloat(valueString) || 0)}
                              >
                                <NumberInputField borderLeftRadius={0} />
                                <NumberInputStepper>
                                  <NumberIncrementStepper />
                                  <NumberDecrementStepper />
                                </NumberInputStepper>
                              </NumberInput>
                            )}
                          />
                        </InputGroup>
                        <FormErrorMessage fontSize="xs">{errors.outros_custos?.message}</FormErrorMessage>
                      </FormControl>
                    </GridItem>
                  </Grid>
                </Box>

                <Divider my={4} />

                {/* Seção de Observações */}
                <Box>
                  <Heading as="h3" size="sm" mb={3} color="gray.700">
                    Observações
                  </Heading>
                  <FormControl isInvalid={!!errors.observacoes}>
                    <Textarea
                      {...register('observacoes')}
                      placeholder="Informações adicionais sobre o produto"
                      size="sm"
                      borderRadius="sm"
                      minH="170px"
                      resize="vertical"
                    />
                    <FormErrorMessage fontSize="xs">{errors.observacoes?.message}</FormErrorMessage>
                  </FormControl>
                </Box>
              </Box>
            </SimpleGrid>
          </form>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderTopColor="gray.100" pt={3} pb={3} justifyContent="space-between">
          <Box>
            <Tag size="sm" colorScheme="gray" borderRadius="sm" mr={2}>
              Última atualização: {produto?.updated_at ? new Date(produto.updated_at).toLocaleString() : 'Não disponível'}
            </Tag>
          </Box>
          <Flex>
            <Button variant="outline" size="sm" mr={3} onClick={onClose} borderRadius="sm">
              Cancelar
            </Button>
            <Button
              type="submit"
              form="editar-produto-form"
              colorScheme="blue"
              size="sm"
              isLoading={isSubmitting || isLoading}
              loadingText="Salvando..."
              isDisabled={!isDirty}
              borderRadius="sm"
            >
              Salvar Alterações
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default ModalEditarProduto
